import React, { useEffect, useState } from 'react'
import { Badge, Col, Row, Tab, Tabs } from 'react-bootstrap'
import DatatableTables from '../../components/Table'
import { SubscriptionProps, TransactionResumeProps } from 'types/global'
import { formatCurrency } from 'util/string'
import SwitchWithLabel from 'components/Switch'
import plans from 'services/plans'
import Swal from 'sweetalert2'
import { exportFile } from 'util/function'
import { Loading } from 'components/Loading'
import { toast } from 'react-toastify'

const columns = [
  { label: 'Identificador', field: 'id' },
  { label: 'Descrição', field: 'name' },
  { label: 'Status', field: 'instanceStatusLabel' },
  { label: 'Data de criação', field: 'createdAt' }
]

const columnsPayments = [
  { label: 'Identificador', field: 'id' },
  { label: 'Vencimento', field: 'competence' },
  { label: 'Status', field: 'statusName', html: true },
  { label: 'Valor Total', field: 'amount' },
  { label: 'Valor Pago', field: 'paymentAmount' }
]

type SubscriptionTabKey = 'instances' | 'payments'

interface SubscriptionCollapseProps {
  subscription: SubscriptionProps
  /** Aba inicial ao abrir (ex.: deep link da página de assinaturas). */
  defaultTab?: SubscriptionTabKey
}

const HeaderTableTitle = ({ title, paid, setPaid }) => {
  return (
    <div className="custom-header">
      <div className="custom-title">{title}</div>
      <div className="custom-check">
        <SwitchWithLabel
          onChange={() => {
            setPaid(!paid)
          }}
          checked={paid}
          label="Pagos"
        />
      </div>
    </div>
  )
}

function formatInstanceStatus(raw?: string): string {
  if (!raw) return '—'
  const s = String(raw).toLowerCase()
  if (s === 'ativo') return 'Ativo'
  if (s === 'inativo') return 'Inativo'
  return raw
}

/** Ex.: 2026-04-20 → 20/04/2026 */
function formatCompetenceBr(raw: string | undefined | null): string {
  if (raw == null || raw === '') return '—'
  const s = String(raw).trim()
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (m) {
    return `${m[3]}/${m[2]}/${m[1]}`
  }
  return s
}

export function SubscriptionCollapse({
  subscription,
  defaultTab
}: SubscriptionCollapseProps) {
  const [activeTab, setActiveTab] = useState<SubscriptionTabKey>(
    defaultTab ?? 'instances'
  )
  const [paymentRows, setPaymentRows] = useState([])
  const [paidFilter, setPaidFilter] = useState(false)
  const [loading, setLoading] = useState(false)
  const [instanceRows, setInstanceRows] = useState<any[]>([])

  useEffect(() => {
    setActiveTab(defaultTab ?? 'instances')
  }, [subscription.id, defaultTab])

  useEffect(() => {
    const list = subscription?.instances || []
    setInstanceRows(
      list.map((row) => ({
        ...row,
        instanceStatusLabel: formatInstanceStatus(row.instanceStatus)
      }))
    )
  }, [subscription])

  const [tableConfig, setTableConfig] = useState({
    options: {
      collapse: false,
      more: false,
      pagination: true
    },
    config: {
      downloadText: 'Download do boleto',
      paymentInvoiceLinkText: 'Abrir link de pagamento'
    },
    total: 0,
    currentPage: 0,
    rowsPerPage: 20
  })

  function handleSuccessPaymentConfirmation(baseUrl: string) {
    exportFile({
      base64: baseUrl,
      extension: '.pdf',
      fileName: 'boleto_zapmais'
    })
  }
  const handleAddPaymentMethodBack = () => {}

  async function handleDownload(row: TransactionResumeProps) {
    try {
      console.log({ row })
      setLoading(true)
      const response = await plans.getSubscriptionInfo(
        subscription.localId,
        'boleto'
      )
      setLoading(false)

      if (response.status === 200) {
        const data = response?.data?.data
        console.log({ data })

        Swal.fire({
          icon: 'success',
          title: response.data.msg?.title || 'Boleto disponível',
          html:
            response.data.msg?.text ||
            '<b>O cliente já consegue fazer isso.</b><br /><br /> Da próxima vez diz pra ele baixar na página de assinaturas',
          confirmButtonText: 'Baixar arquivo',
          showCancelButton: false,
          allowEscapeKey: false,
          allowOutsideClick: false
        }).then(() => handleSuccessPaymentConfirmation(response.data.data.pdf))
      } else {
        Swal.fire({
          icon: 'warning',
          title:
            response?.response?.data?.error?.title ||
            'Não foi possível buscar as informações',
          html:
            response?.response?.data?.error?.text ||
            'As informações para a forma de pagamento escolhida não estão disponíveis',
          confirmButtonText: 'Fechar',
          showCancelButton: false,
          allowEscapeKey: false,
          allowOutsideClick: false
        }).then(handleAddPaymentMethodBack)
      }
    } catch (error) {
      console.log({ error })

      setLoading(false)
    }
  }

  async function handleOpenAsaasPayment(row: TransactionResumeProps) {
    const payId = row?.asaasPaymentId
    if (!payId) {
      toast.error('Identificador da cobrança no Asaas indisponível.')
      return
    }
    setLoading(true)
    try {
      const response = await plans.getAsaasInvoiceUrl(String(payId))
      setLoading(false)
      if (response.status === 200 && response.data?.data?.url) {
        window.open(response.data.data.url as string, '_blank', 'noopener,noreferrer')
        return
      }
      const msg =
        (response as any)?.response?.data?.message ||
        (response as any)?.data?.message ||
        'Não foi possível obter o link de pagamento.'
      toast.error(typeof msg === 'string' ? msg : 'Erro ao abrir o link.')
    } catch (e) {
      setLoading(false)
      toast.error('Erro ao abrir o link de pagamento.')
    }
  }

  useEffect(() => {
    if (subscription?.payments?.length > 0) {
      let payments = subscription.payments
      if (paidFilter) {
        payments = payments.filter((item) => item.status === 'Pago')
      }
      console.log({ payments })

      const newPaymentsRows = payments.map((item) => ({
        ...item,
        competence: formatCompetenceBr(item.competence),
        amount: formatCurrency({ value: item.amount }),
        paymentAmount:
          item.status === 'Pago'
            ? formatCurrency({ value: item.paymentAmount })
            : '—',
        statusName:
          item.status === 'Pago'
            ? `<span class="badge bg-success">${item.status}</span>`
            : `<span class="badge bg-secondary">${item.status}</span>`,
        hideMoreOptions: item.status === 'Pago',
        hidePaymentInvoiceLink:
          item.status !== 'Pendente' || !item.asaasPaymentId
      }))
      console.log({ newPaymentsRows })

      setPaymentRows(newPaymentsRows)
    }
  }, [subscription, paidFilter])

  return (
    <Row className="collapse-subscription">
      <Loading open={loading} />
      <Col>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) =>
            setActiveTab((k as SubscriptionTabKey) || 'instances')
          }
          id={`subscription-tabs-${subscription.id}`}
          className="mb-3"
        >
          <Tab eventKey="instances" title="Instâncias">
            <DatatableTables
              title={'Instâncias nessa assinatura'}
              columns={columns}
              rows={instanceRows}
              tableConfig={tableConfig}
              setTableConfig={setTableConfig}
              emptyText="Nenhuma instância criada até o momento"
              layoutCollapse={true}
            />
          </Tab>
          <Tab eventKey="payments" title="Pagamentos">
            <DatatableTables
              title={
                <HeaderTableTitle
                  title="Cobranças nesta assinatura"
                  paid={paidFilter}
                  setPaid={setPaidFilter}
                />
              }
              columns={columnsPayments}
              rows={paymentRows}
              tableConfig={{
                ...tableConfig,
                options: {
                  more: true,
                  download: true,
                  paymentInvoiceLink: true
                }
              }}
              setTableConfig={setTableConfig}
              emptyText="Nenhum cobrança criada até o momento"
              layoutCollapse={true}
              handleDownload={handleDownload}
              handleOpenAsaasPayment={handleOpenAsaasPayment}
            />
          </Tab>
        </Tabs>
      </Col>
    </Row>
  )
}
